import classNames from 'classnames';

import { type CSSProperties, useCallback, useMemo } from 'react';

import { ButtonIcon } from '../../..';
import { NAVBAR_MENU_ITEM_BUTTON_DEFAULTS, NAVBAR_MENU_ITEM_LINK_DEFAULTS } from '../../../';
import { getBackgroundColorStyle } from '../../../utils/background';
import { cn } from '../../../utils/cn';
import { computeColor } from '../../../utils/colors';
import { computeFont } from '../../../utils/fonts';
import { getFontSizeVariables } from '../../../utils/getFontSizeVariables';
import * as styles from '../../Navbar.css';
import type {
  NavbarMenuItemV2ButtonElementProps,
  NavbarMenuItemV2LinkElementProps,
  TNavbarItemV2ButtonElement,
} from '../NavbarMenuItem.type';

export function NavbarMenuItemLink(props: NavbarMenuItemV2LinkElementProps | NavbarMenuItemV2ButtonElementProps) {
  const { element, className, children, onClick, attributes, ...rest } = props;

  const {
    type,
    href,
    isCustomLink,
    label,
    new_tab,
    customAttributes,
    padding,
    background,
    borderRadius,
    color,
    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverTextColor,
    hoverBackgroundColor,
    hoverUnderline,
  } = element.attrs || {};

  const {
    icon = undefined,
    iconPlacement = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.iconPlacement,
    iconColor = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.iconColor,
    iconSize = NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.iconSize,
    hoverIconColor = iconColor,
  } = element.attrs?.type === 'button' ? element.attrs || {} : {};

  const { fontFamily, fontSize, fontWeight } = element.attrs || {};
  // Type cast only to fix type error
  const { letterSpacing } = (element.attrs as TNavbarItemV2ButtonElement['attrs']) || {};

  const visible = element.attrs?.visible ?? true;

  const cleanHref = useMemo(() => {
    try {
      const hrefURL = href ? new URL(href) : undefined;
      if (href && hrefURL && !isCustomLink) {
        return href.replace(hrefURL.origin, '');
      }
    } catch (_e) {}
    return href;
  }, [href, isCustomLink]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (!onClick) return;
      e.preventDefault();
      onClick();
    },
    [onClick],
  );

  if (element.attrs?.type !== 'link' && element.attrs?.type !== 'button') {
    return null;
  }

  if (!visible) {
    return null;
  }

  const DEFAULTS = type === 'button' ? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS : NAVBAR_MENU_ITEM_LINK_DEFAULTS;
  const classStyle = type === 'button' ? styles.navbarItemBaseV2 : styles.navbarItemLinkV2;

  let buttonStyles: CSSProperties = {};

  if (type === 'button') {
    buttonStyles = {
      ...buttonStyles,
      '--border-color': borderColor ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.borderColor,
      '--border-width': borderWidth ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.borderWidth,
      '--border-style': borderStyle ?? NAVBAR_MENU_ITEM_BUTTON_DEFAULTS.borderStyle,
      '--background-color': getBackgroundColorStyle(background ?? DEFAULTS.background).background,
    } as CSSProperties;
  }

  const isNoText = !label || label?.length === 0;

  return (
    <div className={classNames(styles.navbarItemLinkContainerV2)} {...attributes}>
      <a
        {...rest}
        {...customAttributes}
        onClick={handleClick}
        className={classNames(classStyle, className, customAttributes?.className)}
        style={
          {
            ...buttonStyles,
            '--border-radius': borderRadius ?? DEFAULTS.borderRadius,
            '--padding': padding ?? DEFAULTS.padding,
            '--text-color': computeColor(color ?? DEFAULTS.color),
            '--font-family': computeFont(fontFamily) || DEFAULTS.fontFamily,
            ...getFontSizeVariables(fontSize ?? DEFAULTS.fontSize),
            '--font-weight': fontWeight ?? DEFAULTS.fontWeight,
            '--letter-spacing': letterSpacing ?? DEFAULTS.letterSpacing,

            '--hover-border-radius': hoverBorderRadius || borderRadius || DEFAULTS.borderRadius,
            '--hover-text-color': hoverTextColor || color || DEFAULTS.color,
            '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.background,
            '--hover-border-color': hoverBorderColor || borderColor || DEFAULTS.borderColor,
            '--hover-border-style': hoverBorderStyle || borderStyle || DEFAULTS.borderStyle,
            '--hover-border-width': hoverBorderWidth || borderWidth || DEFAULTS.borderWidth,

            '--hover-underline': hoverUnderline ? 'underline' : 'none',
          } as CSSProperties
        }
        href={cleanHref}
        target={new_tab ? '_blank' : '_self'}
      >
        {icon && iconPlacement === 'left' ? (
          <ButtonIcon
            icon={icon}
            color={iconColor}
            hoverColor={hoverIconColor}
            data-placement={iconPlacement}
            data-no-text={isNoText}
            className={cn('p-0', styles.iconV2)}
            size={iconSize}
          />
        ) : null}
        {children}
        {icon && iconPlacement === 'right' ? (
          <ButtonIcon
            color={iconColor}
            hoverColor={hoverIconColor}
            icon={icon}
            data-placement={iconPlacement}
            data-no-text={isNoText}
            className={cn('p-0', styles.iconV2)}
            size={iconSize}
          />
        ) : null}
      </a>
    </div>
  );
}
